import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Share2,
    Gift,
    IndianRupee,
    ArrowRight,
    Zap,
    Search,
    Copy,
    Check,
    ExternalLink
} from 'lucide-react';

const ReferPage = () => {
    const { trackId } = useParams();
    const navigate = useNavigate();

    const [offerData, setOfferData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [upiId, setUpiId] = useState('');
    const [generatedData, setGeneratedData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);
    const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(5);

    const getFallbackTelegramLink = () => 'https://t.me/yourchannel';

    const handleOfferNotFound = () => {
        setError('Offer not found or unavailable.');
    };

    useEffect(() => {
        const fetchOfferTrackingDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const baseUrl = process.env.REACT_APP_API_URL || '';

                const response = await axios.get(`${baseUrl}/api/public/track/${trackId}`);

                const resData = response.data?.data || response.data;

                if (resData) {
                    // Check for inactive status
                    const status = String(resData.status || '').toLowerCase();
                    if (status === 'inactive' || resData.isActive === false) {
                        setIsInactiveModalOpen(true);
                        const channelUrl = resData.channel || getFallbackTelegramLink();
                        let count = 5;
                        const timer = setInterval(() => {
                            count -= 1;
                            setRedirectCountdown(count);
                            if (count <= 0) {
                                clearInterval(timer);
                                window.location.href = channelUrl;
                            }
                        }, 1000);
                        setLoading(false);
                        return;
                    }

                    setOfferData(resData);

                } else {
                    handleOfferNotFound();
                }
            } catch (err) {
                console.error('Error fetching offer tracking details:', err);
                handleOfferNotFound();
            } finally {
                setLoading(false);
            }
        };

        if (trackId) {
            fetchOfferTrackingDetails();
        } else {
            setLoading(false);
        }
    }, [trackId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!upiId) return;

        try {
            setSubmitting(true);
            setError(null);
            const baseUrl = process.env.REACT_APP_API_URL || '';

            const response = await axios.get(`${baseUrl}/api/public/refer/generate/${trackId}/${upiId}`);

            if (response.data && response.data.status) {
                setGeneratedData(response.data.data);
            } else if (response.data) {
                setGeneratedData(response.data);
            } else {
                setError('Failed to generate referral link.');
            }
        } catch (err) {
            console.error('Error generating link:', err);
            setError('An error occurred while generating your link. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (generatedData?.referLink) {
            navigator.clipboard.writeText(generatedData.referLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleTrackLead = () => {
        const activeTrackId = trackId || generatedData?.trackId || offerData?.trackId;
        if (activeTrackId) {
            navigate(`/refer-track/${activeTrackId}`);
        }
    };

    return (
        <div className="w-full bg-zinc-950 text-zinc-100 pt-6 sm:pt-12 lg:pt-16 pb-24 px-4 sm:px-8 relative overflow-hidden font-sans min-h-screen selection:bg-emerald-500 selection:text-zinc-950">
            <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(1.1); }
        }

        @keyframes borderRotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes floatEffect {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.94); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .animate-spring-in {
          animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-scale-in {
          animation: scaleIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 7s ease-in-out infinite;
        }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.6) 0%, rgba(24, 24, 27, 0.9) 40%, rgba(20, 184, 166, 0.6) 100%);
          background-size: 200% 200%;
          animation: borderRotate 5s ease infinite;
        }

        .highlight-card-glow {
          box-shadow: 0 0 35px -5px rgba(16, 185, 129, 0.25), 0 20px 25px -5px rgba(0, 0, 0, 0.5);
        }

        .smooth-transition {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }

        .float-animation {
          animation: floatEffect 4s ease-in-out infinite;
        }

        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }
      `}</style>

            {/* Ambient background blur circles */}
            <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none ambient-glow" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />

            <div className="max-w-[850px] mx-auto space-y-8 relative z-10">

                {/* ========== SKELETON LOADING ========== */}
                {loading && (
                    <div className="space-y-8 animate-scale-in">
                        {/* Skeleton: Offer Details Card */}
                        <div className="relative p-[2px] rounded-[2.5rem] bg-zinc-800/60">
                            <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 sm:p-10 rounded-[2.4rem] relative z-10 space-y-8">
                                {/* Logo + Name skeleton */}
                                <div className="flex flex-row items-center gap-5 sm:gap-7">
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-zinc-800 rounded-[1.8rem] shimmer-bg shrink-0" />
                                    <div className="flex-1 space-y-3">
                                        <div className="w-28 h-6 bg-zinc-800 rounded-full shimmer-bg" />
                                        <div className="w-52 h-8 bg-zinc-800 rounded-lg shimmer-bg" />
                                        <div className="flex gap-6 pt-1">
                                            <div className="space-y-1">
                                                <div className="w-14 h-3 bg-zinc-800 rounded shimmer-bg" />
                                                <div className="w-20 h-7 bg-zinc-800 rounded-md shimmer-bg" />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="w-20 h-3 bg-zinc-800 rounded shimmer-bg" />
                                                <div className="w-28 h-7 bg-zinc-800 rounded-md shimmer-bg" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Form skeleton */}
                                <div className="p-6 sm:p-8 bg-zinc-950/90 rounded-[2rem] border border-zinc-800/80 space-y-5">
                                    <div className="space-y-2">
                                        <div className="w-44 h-6 bg-zinc-800 rounded shimmer-bg" />
                                        <div className="w-72 h-4 bg-zinc-800 rounded shimmer-bg" />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="w-full h-13 bg-zinc-800 rounded-xl shimmer-bg" />
                                        <div className="w-full h-13 bg-zinc-800 rounded-xl shimmer-bg" />
                                    </div>
                                    <div className="w-full h-14 bg-zinc-800 rounded-xl shimmer-bg" />
                                </div>
                            </div>
                        </div>

                        {/* Skeleton: Accordion 1 */}
                        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden">
                            <div className="px-6 sm:px-8 py-5 flex items-center gap-3">
                                <div className="w-8 h-8 bg-zinc-800 rounded-xl shimmer-bg" />
                                <div className="w-28 h-4 bg-zinc-800 rounded shimmer-bg" />
                            </div>
                            <div className="px-6 sm:px-8 pb-8 space-y-3.5 border-t border-zinc-800/50 pt-4">
                                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
                                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
                                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
                            </div>
                        </div>

                        {/* Skeleton: Accordion 2 */}
                        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden">
                            <div className="px-6 sm:px-8 py-5 flex items-center gap-3">
                                <div className="w-8 h-8 bg-zinc-800 rounded-xl shimmer-bg" />
                                <div className="w-40 h-4 bg-zinc-800 rounded shimmer-bg" />
                            </div>
                            <div className="px-6 sm:px-8 pb-8 space-y-3 border-t border-zinc-800/50 pt-4">
                                <div className="w-full h-4 bg-zinc-800/60 rounded shimmer-bg" />
                                <div className="w-5/6 h-4 bg-zinc-800/60 rounded shimmer-bg" />
                                <div className="w-4/6 h-4 bg-zinc-800/60 rounded shimmer-bg" />
                            </div>
                        </div>
                    </div>
                )}

                {/* ========== ERROR STATE ========== */}
                {!loading && error && (
                    <div className="text-center py-20 bg-zinc-900/80 rounded-[2.5rem] border border-zinc-800 backdrop-blur-2xl animate-spring-in space-y-4 px-6">
                        <div className="w-14 h-14 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center mx-auto text-2xl">
                            ⚠️
                        </div>
                        <p className="text-zinc-200 text-base font-semibold">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-xl text-xs font-bold border border-zinc-700 smooth-transition micro-interaction"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* ========== REFER CONTENT ========== */}
                {!loading && !error && offerData && (
                    <div className="space-y-8 animate-scale-in">

                        {/* 1. HIGHLIGHTED REFER DETAILS SECTION */}
                        <div className="relative p-[2px] rounded-[2.5rem] highlight-border highlight-card-glow smooth-transition group">
                            <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 sm:p-10 rounded-[2.4rem] relative z-10 space-y-8">

                                {/* Offer Logo Beside Offer Name (Horizontal Layout) */}
                                <div className="flex flex-row items-center gap-5 sm:gap-7 text-left">
                                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-zinc-950 rounded-[1.8rem] border border-emerald-500/30 flex items-center justify-center shadow-inner shrink-0 overflow-hidden float-animation group-hover:border-emerald-400 smooth-transition">
                                        {offerData?.logo ? (
                                            <img
                                                src={offerData.logo}
                                                alt={offerData?.name || 'Campaign'}
                                                className="w-full h-full object-cover rounded-[1.8rem]"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(offerData?.name || 'Offer') + '&background=059669&color=fff';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-zinc-800/50 rounded-[1.8rem] animate-pulse" />
                                        )}
                                    </div>

                                    <div className="flex-1 space-y-2 min-w-0">
                                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                                            <Gift className="w-3.5 h-3.5" />
                                            Referral Reward Active
                                        </div>

                                        <h1 className="text-2xl sm:text-3xl font-black text-zinc-100 tracking-tight leading-tight group-hover:text-emerald-400 smooth-transition truncate">
                                            {offerData?.name || 'Loading offer details...'}
                                        </h1>

                                        <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-1">

                                            <div>
                                                <p className="text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest">Track Reference</p>
                                                <p className="text-xs sm:text-sm font-mono font-bold text-zinc-300 truncate max-w-[140px] mt-1 bg-zinc-950 px-2.5 py-1 rounded-lg border border-zinc-800/80">
                                                    #{trackId || offerData?.trackId || 'fa48dbbe4c74'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* REFER FORM */}
                                <div className="p-6 sm:p-8 bg-zinc-950/90 rounded-[2rem] border border-zinc-800/80 shadow-2xl relative overflow-hidden group/form">
                                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover/form:scale-110 smooth-transition pointer-events-none">
                                        <Share2 className="w-32 h-32 text-emerald-400" />
                                    </div>

                                    <div className="relative z-10 space-y-5">
                                        <div>
                                            <h3 className="text-zinc-100 text-lg font-black tracking-tight">Generate Your Referral Link</h3>
                                            <p className="text-zinc-400 text-xs font-medium mt-1">
                                                Provide your UPI ID to generate a unique link and start earning commissions.
                                            </p>
                                        </div>

                                        {isInactiveModalOpen && (
                                            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-center animate-spring-in">
                                                <p className="text-xs font-bold text-amber-400">This offer is currently inactive.</p>
                                                <p className="text-[10px] text-amber-500/80 mt-1">Redirecting in {redirectCountdown} seconds...</p>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-medium flex items-center gap-2 animate-spring-in">
                                                <span className="shrink-0">⚠️</span>
                                                <span>{error}</span>
                                            </div>
                                        )}

                                        {!generatedData ? (
                                            <form onSubmit={handleSubmit} className="space-y-5 animate-spring-in">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                                        Payout UPI ID
                                                    </label>
                                                    <div className="relative group/input">
                                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400">
                                                            <IndianRupee className="w-4 h-4" />
                                                        </div>
                                                        <input
                                                            required
                                                            type="text"
                                                            placeholder="yourname@upi"
                                                            value={upiId}
                                                            onChange={(e) => setUpiId(e.target.value)}
                                                            className="w-full pl-11 pr-4 py-3.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-100 font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 smooth-transition text-xs placeholder:text-zinc-600"
                                                        />
                                                    </div>
                                                    <p className="text-[9px] text-zinc-500 font-medium ml-1">
                                                        * Referral earnings will be sent to this UPI.
                                                    </p>
                                                </div>

                                                <button
                                                    type="submit"
                                                    disabled={submitting || isInactiveModalOpen}
                                                    className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 smooth-transition micro-interaction disabled:opacity-50"
                                                >
                                                    {submitting ? (
                                                        <span>Generating...</span>
                                                    ) : (
                                                        <>
                                                            <span>Generate My Link</span>
                                                            <ArrowRight className="w-4 h-4" />
                                                        </>
                                                    )}
                                                </button>
                                            </form>
                                        ) : (
                                            <div className="space-y-5 animate-spring-in">
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                                                        Your Exclusive Referral Link
                                                    </label>
                                                    <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 p-2 rounded-xl">
                                                        <input
                                                            readOnly
                                                            value={generatedData.referLink}
                                                            className="w-full bg-transparent px-3 py-2 text-xs font-bold text-zinc-100 outline-none truncate"
                                                        />
                                                        <button
                                                            onClick={handleCopy}
                                                            className="px-4 py-3 bg-emerald-500 text-zinc-950 rounded-xl text-xs font-black flex items-center gap-1.5 shrink-0 hover:bg-emerald-400 active:scale-95 transition-all shadow-md shadow-emerald-500/20 micro-interaction"
                                                        >
                                                            {copied ? (
                                                                <>
                                                                    <Check className="w-3.5 h-3.5" />
                                                                    Copied
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy className="w-3.5 h-3.5" />
                                                                    Copy
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                {generatedData.referCode && (
                                                    <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-2xl flex items-center justify-between">
                                                        <div>
                                                            <span className="text-[10px] font-black uppercase text-zinc-500 block">Refer Code</span>
                                                            <span className="font-mono font-bold text-zinc-100 text-sm">Redirect</span>
                                                        </div>
                                                        <a
                                                            href={generatedData.referLink}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 transition-colors shadow-sm micro-interaction"
                                                        >
                                                            <ExternalLink className="w-4 h-4" />
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* 2. PRO TIP ACCORDION-LIKE SECTION */}
                        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden shadow-2xl animate-spring-in smooth-transition hover:border-zinc-700">
                            <div className="px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-zinc-800/50">
                                <div className="w-8 h-8 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-xl flex items-center justify-center shrink-0">
                                    <Zap className="w-4 h-4" />
                                </div>
                                <span className="font-black text-xs uppercase tracking-widest text-zinc-200">Pro Tip</span>
                            </div>
                            <div className="px-6 sm:px-8 pb-8 pt-4">
                                <p className="text-xs font-medium text-zinc-400 leading-relaxed">
                                    Share this link on WhatsApp groups or Telegram to get the highest conversion rates!
                                </p>
                            </div>
                        </div>

                    </div>
                )}

            </div>

            {/* FLOATING TRACK REFER BUTTON */}
            <div className="fixed bottom-6 right-6 z-40">
                <button
                    onClick={handleTrackLead}
                    className="flex items-center gap-2.5 px-5 py-3.5 bg-zinc-900 border border-zinc-700/80 hover:border-emerald-500/50 text-zinc-100 rounded-full font-black text-xs uppercase tracking-widest shadow-2xl shadow-black hover:bg-zinc-800 smooth-transition micro-interaction group"
                >
                    <Search className="w-4 h-4 text-emerald-400 group-hover:rotate-12 smooth-transition" />
                    Track Refer
                </button>
            </div>

            {/* OFFER INACTIVE MODAL */}
            {isInactiveModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
                    <div className="relative w-full max-w-md bg-zinc-900 border border-amber-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6">
                        <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center mx-auto float-animation">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>

                        <div className="space-y-2">
                            <h3 className="text-xl font-black text-zinc-100 tracking-tight">Offer Currently Inactive</h3>
                            <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                                This campaign is temporarily paused or completed. Redirecting you to our official channel for active updates...
                            </p>
                        </div>

                        <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
                            <p className="text-amber-400 text-xs font-mono font-bold">
                                Redirecting to Channel in {redirectCountdown}s
                            </p>
                        </div>

                        <button
                            onClick={() => window.location.href = offerData?.channel || getFallbackTelegramLink()}
                            className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
                        >
                            <span>Go to Channel Now</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ReferPage;